import React from 'react';
import { useTranslation } from 'react-i18next';
import { useInRouterContext, useNavigate } from 'react-router-dom';

import { Icon, Skeleton, Text } from '@/nova/components';
import { useFeatureGate } from '@/nova/hooks/useFeatureGate';
import { ROUTE } from '@/nova/routes';
import { useAppSelector } from '@/store';

import {
  listArtStyles,
  type ArtStyleMascot,
  type ArtStyleResult,
  type ArtStyleSummary,
  type MascotPick,
} from '../../../art-style/art-style-api';
import { ArtStylePicker } from '../../../art-style/components/ArtStylePicker';
import { MascotPicker } from '../../../art-style/components/MascotPicker';
import { artStyleLabel, useActiveArtStyleState, type ActiveArtStyleState } from '../../../art-style/useActiveArtStyle';
import { pickedMascotOf, useStyleCast, useStyleChoices } from '../../../art-style/useStyleChoice';
import type { AdVersionArtStyle } from '../../banner-kit-api';
import {
  adCast,
  adCharacterSummary,
  adStyleAllowsNone,
  adStyleChoiceOf,
  adStylePickerShown,
  adStyleSummary,
  resolveAdStyle,
  type AdDrawnStyle,
  type AdStyleChoice,
} from './ad-art-style';
import * as S from './AdArtStyle.styles';

/** What an ad composer needs to know about the brand's active art style. */
export type AdArtStyleInfo = {
  /** The active style's name as words, or null while there is none (or it is still being read). */
  styleName: string | null;
  /** The style has a cast an ad can draw (a mascot with an image): only then does a Character section show. */
  hasCast: boolean;
  /** A closed Character section's summary for a pick ('' auto, 'none', or a mascot id). */
  characterSummary: (pick: MascotPick) => string;
  /** A closed Art style section's summary ('' while the style is still being read). */
  styleSummary: string;
  /** The active style is flagged mature (false with no active style, or while it is still being read). */
  mature: boolean;
  /** The brand's active style has been read (so `mature` is known: a render's model and price follow it). */
  loaded: boolean;
  /** The read failed: a style may be active, so no run reuses or prices anything against "no style". */
  failed: boolean;
  /** Read and answered: what a run's reuse and price may follow (loaded, not failed). */
  ready: boolean;
  /** The active style's id ('' with no active style, or while it is still being read): art drawn with another style
   *  is not reused by a run. */
  styleId: string;
};

/**
 * The display brand's active art style, for an ad composer working on `tenantId`. The style is the display brand's
 * (the one spark draws the brand's ads with, as the generate sheets read it); a composer working on another brand
 * than the one displayed sees no style rather than the wrong one.
 */
const useAdActiveStyle = (tenantId?: number): ActiveArtStyleState => {
  const state = useActiveArtStyleState();
  const displayTenant = useAppSelector((root) => Number(root.casinoInstance?.displayBrand?.tenant_id));
  if (tenantId !== undefined && tenantId !== displayTenant) return { active: null, loaded: true, failed: false };
  return state;
};

/** The brand's active art style for an ad composer's rail: its name, whether it has a cast, and the closed sections'
 *  summaries. */
export const useAdArtStyle = (tenantId?: number): AdArtStyleInfo => {
  const { t } = useTranslation();
  const { active, loaded, failed } = useAdActiveStyle(tenantId);
  const cast = adCast(active?.mascots);
  const styleName = active ? artStyleLabel(active.name) || t('Your art style') : null;
  return {
    styleName,
    hasCast: cast.length > 0,
    characterSummary: (pick: MascotPick): string => adCharacterSummary(cast, pick, t),
    styleSummary: adStyleSummary(loaded, styleName, t, failed),
    mature: active?.mature === true,
    loaded,
    failed,
    ready: loaded && !failed,
    styleId: active?.id ?? '',
  };
};

/** An ad run's art style: the brand's styles to pick from, the owner's pick (style and character) and what follows
 *  from it. Everything in AdArtStyleInfo describes the PICKED style here (its name, cast, mature flag, id). */
export type AdStyleRun = AdArtStyleInfo & {
  /** The styles a run can draw in (each with a definition), the primary first; empty while reading, when the brand
   *  has none, or when the list could not be read. */
  styles: ArtStyleSummary[];
  /** The picker shows: it offers more than one choice. */
  showPicker: boolean;
  /** "No style" is one of the choices (the brand has styles but no primary). */
  allowNone: boolean;
  /** The picked style's list entry (undefined for no style, or when the list could not be read). */
  picked: ArtStyleSummary | undefined;
  /** The picked style's cast a run can draw (mascots with an image); empty while it is read. */
  cast: ArtStyleMascot[];
  /** The character the run draws: the owner's pick while it was made for the picked style, else Auto. */
  mascotPick: MascotPick;
  /** The choice as held: keep it per version (a composer's version switch) and give it back with setChoice. */
  choice: AdStyleChoice;
  setChoice: (next: AdStyleChoice) => void;
  /** Pick a style ('' for "No style"). The character pick made for another style stops counting (and counts again
   *  when that style is picked back). */
  pickStyle: (styleId: string) => void;
  /** Pick a character for the picked style. */
  pickMascot: (pick: MascotPick) => void;
  /** The request's `style_id`: the picked style's id, always (undefined for no style). */
  sendStyleId: string | undefined;
  /** A listed style by id (e.g. a version's, to price a redraw that keeps its art). */
  styleOf: (styleId: string | undefined) => ArtStyleSummary | undefined;
};

/**
 * The art style an ad composer's next run draws in, picked per run like the character (never saved in the document):
 * seeded from `seed` (the newest version's `art_style`: its style while the brand still lists it, and its character,
 * made for that style), else the brand's primary style. The list is read when the composer mounts. When it cannot be
 * read the run follows the active style read on its own (as before the pick existed).
 */
export const useAdStyleRun = (tenantId: number | undefined, seed?: AdDrawnStyle): AdStyleRun => {
  const { t } = useTranslation();
  const activeState = useAdActiveStyle(tenantId);
  const choices = useStyleChoices(tenantId);
  const [choice, setChoice] = React.useState<AdStyleChoice>(() => adStyleChoiceOf(seed));
  const active = activeState.active;
  const styleId = resolveAdStyle(choice.style, choices.styles, choices.defaultId, active?.id ?? '');
  const picked = choices.styles.find((style) => style.id === styleId);
  // The list could not be read: the run follows the active style read on its own.
  const fromActive = !picked && styleId !== '' && active?.id === styleId ? active : null;
  const pickedCast = useStyleCast(tenantId, picked?.id);
  const cast = adCast(picked ? pickedCast : fromActive?.mascots);
  // A read list answers everything; without one, the active style's own read decides.
  const answered = choices.loaded && !choices.failed;
  const loaded = choices.loaded && (answered || activeState.loaded);
  const failed = loaded && !answered && activeState.failed;
  const showPicker = adStylePickerShown(choices.styles, choices.defaultId);
  const rawName = picked?.name ?? fromActive?.name;
  const styleName = rawName !== undefined ? artStyleLabel(rawName) || t('Your art style') : null;
  const pickStyle = React.useCallback((next: string): void => setChoice((cur) => ({ ...cur, style: next })), []);
  const pickMascot = React.useCallback(
    (pick: MascotPick): void => setChoice((cur) => ({ ...cur, mascot: { style: styleId, pick } })),
    [styleId],
  );
  return {
    styleName,
    hasCast: cast.length > 0,
    characterSummary: (pick: MascotPick): string => adCharacterSummary(cast, pick, t),
    styleSummary:
      loaded && !failed && styleName === null && showPicker
        ? t('No style')
        : adStyleSummary(loaded, styleName, t, failed),
    mature: picked ? picked.mature : fromActive?.mature === true,
    loaded,
    failed,
    ready: loaded && !failed,
    styleId,
    styles: choices.styles,
    showPicker,
    allowNone: adStyleAllowsNone(choices.styles, choices.defaultId),
    picked,
    cast,
    mascotPick: pickedMascotOf(choice.mascot, styleId),
    choice,
    setChoice,
    pickStyle,
    pickMascot,
    sendStyleId: styleId || undefined,
    styleOf: (id) => (id ? choices.styles.find((style) => style.id === id) : undefined),
  };
};

/* The style list carries each style's thumbnail (its newest sample, else its first mood image); the active-style
 * hook keeps only its name and cast, so the section reads the list once more for the picture. Reads in flight are
 * shared per brand. */
const thumbInFlight = new Map<number, Promise<ArtStyleResult<ArtStyleSummary[]>>>();

const readStyles = (tenantId: number): Promise<ArtStyleResult<ArtStyleSummary[]>> => {
  const held = thumbInFlight.get(tenantId);
  if (held) return held;
  const next = listArtStyles(tenantId).finally(() => thumbInFlight.delete(tenantId));
  thumbInFlight.set(tenantId, next);
  return next;
};

/** The thumbnail of the brand's style `styleId`, or '' (none, not read yet, or the read failed). */
const useStyleThumb = (tenantId: number | undefined, styleId: string | undefined): string => {
  const [thumb, setThumb] = React.useState<{ key: string; url: string } | null>(null);
  const key = tenantId && styleId ? `${tenantId}:${styleId}` : '';
  React.useEffect(() => {
    if (!tenantId || !styleId) return undefined;
    let live = true;
    void readStyles(tenantId).then((result) => {
      if (!live || !result.ok) return;
      const url = result.value.find((style) => style.id === styleId)?.thumb_url ?? '';
      setThumb({ key: `${tenantId}:${styleId}`, url });
    });
    return () => {
      live = false;
    };
  }, [tenantId, styleId]);
  // A thumbnail read for an earlier brand or style is never shown for this one.
  return thumb && thumb.key === key ? thumb.url : '';
};

/** A text link to Website, Art Style. Mounted only inside a router (a test harness may have none). */
const ArtStyleLink = ({ children }: { children: React.ReactNode }): React.ReactElement => {
  const navigate = useNavigate();
  return (
    <S.ManageLink type="button" onClick={() => navigate(ROUTE.artStyle())}>
      {children}
      <Icon name="arrow-right-circle" size={14} />
    </S.ManageLink>
  );
};

export type AdArtStyleSectionProps = {
  tenantId?: number;
  /** The run's art style (useAdStyleRun): the section then shows the PICKED style, with the picker when the brand
   *  offers a choice. Without it the section shows the brand's active style, read-only. */
  run?: AdStyleRun;
  /** The picker's change handler (default `run.pickStyle`): a composer that gates edits passes its own. */
  onPickStyle?: (styleId: string) => void;
  /** A run is starting or in flight, or the selected version is locked: the picker stays, greyed. */
  disabled?: boolean;
};

/**
 * An ad composer's "Art style" section body: the style the artwork is drawn in (a sample thumbnail, its name, and
 * that it steers the artwork's look and colours), with a link to Website, Art Style where styles are managed (the
 * link shows only where that tab is turned on). Given the run's style (`run`), it shows the picked one and, when the
 * brand has more than one usable style, the Art style picker above it; otherwise the brand's active style, read-only.
 * With no style it says so, as the artwork then follows the subject's key art.
 */
export const AdArtStyleSection = ({
  tenantId,
  run,
  onPickStyle,
  disabled = false,
}: AdArtStyleSectionProps): React.ReactElement => {
  const { t } = useTranslation();
  const activeState = useAdActiveStyle(tenantId);
  const shown = run
    ? {
        loaded: run.loaded,
        failed: run.failed,
        id: run.styleId,
        name: run.styleName,
        // The list entry carries the thumbnail ('' = none); a style the list could not name reads it below.
        thumbUrl: run.picked ? run.picked.thumb_url : undefined,
      }
    : {
        loaded: activeState.loaded,
        failed: activeState.failed,
        id: activeState.active?.id ?? '',
        name: activeState.active ? artStyleLabel(activeState.active.name) || t('Your art style') : null,
        thumbUrl: undefined,
      };
  const readThumb = useStyleThumb(tenantId, shown.thumbUrl === undefined && shown.id ? shown.id : undefined);
  const thumb = shown.thumbUrl ?? readThumb;
  const tabOn = useFeatureGate().isEnabled('art-style');
  const inRouter = useInRouterContext();
  const canLink = tabOn && inRouter;

  if (!shown.loaded) return <Skeleton block height={74} radius={12} />;

  // A failed read is never "no style": a style may well be active.
  if (shown.failed) {
    return (
      <S.Body>
        <S.Note text="Couldn't be read. Reload the page to try again." />
      </S.Body>
    );
  }

  const picker =
    run && run.showPicker ? (
      <S.PickerRow>
        <ArtStylePicker
          styles={run.styles}
          value={run.styleId}
          allowNone={run.allowNone}
          onChange={onPickStyle ?? run.pickStyle}
          disabled={disabled}
        />
      </S.PickerRow>
    ) : null;

  if (!shown.id || shown.name === null) {
    return (
      <S.Body>
        {picker}
        <S.Note text="No art style is active, so the artwork follows your subject's key art." />
        {canLink ? (
          <ArtStyleLink>
            <Text tag="span" text="Set up an art style" />
          </ArtStyleLink>
        ) : null}
      </S.Body>
    );
  }

  return (
    <S.Body>
      {picker}
      <S.StyleCard>
        {thumb ? (
          <S.Thumb src={thumb} alt="" />
        ) : (
          <S.ThumbFallback aria-hidden="true">
            <Icon name="palette" size={18} />
          </S.ThumbFallback>
        )}
        <S.StyleCopy>
          <S.StyleName raw text={shown.name} />
          <S.StyleNote text="Lumio draws the artwork in this style, with its look and colours." />
        </S.StyleCopy>
      </S.StyleCard>
      <S.Note text="Your logo, copy and button are not restyled." />
      {canLink ? (
        <ArtStyleLink>
          <Text tag="span" text="Manage art style" />
        </ArtStyleLink>
      ) : null}
    </S.Body>
  );
};

export type AdCharacterSectionProps = {
  tenantId?: number;
  /** The pick the next run sends as `mascot_id`: '' (auto), 'none' or a mascot's id. */
  value: MascotPick;
  onChange: (value: MascotPick) => void;
  /** A run is starting or in flight: the picker stays, greyed. */
  disabled?: boolean;
  /** The cast to list: the PICKED style's (`useAdStyleRun().cast`). Without it, the active style's. */
  cast?: ArtStyleMascot[];
};

/**
 * An ad composer's "Character" section body: which of the brand's characters (the picked style's cast, else the
 * active style's) the artwork features, Auto by default. Renders nothing while that cast has no character with an
 * image (the composer then leaves the section out).
 */
export const AdCharacterSection = ({
  tenantId,
  value,
  onChange,
  disabled = false,
  cast,
}: AdCharacterSectionProps): React.ReactElement | null => {
  const { hasCast: activeHasCast } = useAdArtStyle(tenantId);
  const hasCast = cast ? adCast(cast).length > 0 : activeHasCast;
  if (!hasCast) return null;
  return (
    <S.Body>
      <S.Note text="Which of your characters appears. Auto picks a different one each time." />
      <S.PickerRow>
        <MascotPicker value={value} onChange={onChange} disabled={disabled} cast={cast} />
      </S.PickerRow>
    </S.Body>
  );
};

export type AdDrawnWithProps = {
  /** The selected version's record of what its art was drawn with (absent: drawn with no art style, or not yet). */
  artStyle?: AdVersionArtStyle;
};

/** "Drawn with <style>" (and its character) for the selected version, as named when it was drawn. */
export const AdDrawnWith = ({ artStyle }: AdDrawnWithProps): React.ReactElement | null => {
  const { t } = useTranslation();
  if (!artStyle?.style_id) return null;
  const style = artStyleLabel(artStyle.style_name ?? '') || t('Your art style');
  const character = artStyle.mascot_id ? artStyle.mascot_name?.trim() || t('Unnamed character') : '';
  return (
    <S.DrawnWith>
      <Icon name="palette" size={13} />
      {character ? (
        <S.DrawnWithText text="Drawn with {{style}} and {{character}}" placeholders={{ style, character }} />
      ) : (
        <S.DrawnWithText text="Drawn with {{style}}" placeholders={{ style }} />
      )}
    </S.DrawnWith>
  );
};
